import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";


// Field definition (must match backend PROJECT_FIELDS in pdf_common.py).
const FIELDS = [
  { key: "rfq_no",           label: "RFQ No." },
  { key: "rfq_name",         label: "RFQ Name" },
  { key: "po_no",            label: "PO No." },
  { key: "rfp_no",           label: "RFP No." },
  { key: "rfp_name",         label: "RFP Name" },
  { key: "type_of_project",  label: "Type of Project" },
  { key: "contract_no",      label: "Contract No." },
  { key: "contract_name",    label: "Contract Name" },
  { key: "submission_date",  label: "RFQ/RFP Submission Date", type: "date" },
];


/**
 * Editable "Project details" card mounted on Invoice / Quotation / DO detail
 * pages. Persists the whole `project_details` dict via a single PATCH call
 * on the parent doc. Empty fields are preserved as blanks — the PDF renderer
 * only shows the ones with values.
 */
export function ProjectDetailsCard({
  docId,
  initial,
  patchFn,          // async (id, {project_details}) => updatedDoc
  invalidateKeys,   // array of react-query keys to invalidate after save
  testIdPrefix = "project-details",
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm({ ...(initial || {}) });
    setDirty(false);
  }, [initial]);

  const mut = useMutation({
    mutationFn: () => patchFn(docId, { project_details: form }),
    onSuccess: () => {
      toast.success("Project details saved");
      setDirty(false);
      (invalidateKeys || []).forEach((k) => qc.invalidateQueries({ queryKey: k }));
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Failed to save");
    },
  });

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  }

  return (
    <section
      data-testid={`${testIdPrefix}-card`}
      className="border border-border rounded-md bg-white mb-5"
    >
      <header className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-4 text-primary" />
          <h3 className="text-sm font-semibold">Project details</h3>
          <span className="text-[10px] text-muted-foreground">
            Renders on the PDF · empty fields are hidden
          </span>
        </div>
        {dirty && (
          <span data-testid={`${testIdPrefix}-dirty-badge`}
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200">
            Unsaved changes
          </span>
        )}
      </header>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="overline">{f.label}</Label>
            {f.type === "date" ? (
              <DatePicker
                testId={`${testIdPrefix}-${f.key}`}
                value={form[f.key] || ""}
                onChange={(v) => setField(f.key, v || "")}
              />
            ) : (
              <Input
                data-testid={`${testIdPrefix}-${f.key}`}
                type={f.type || "text"}
                value={form[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder="Enter a value…"
              />
            )}
          </div>
        ))}
      </div>
      <footer className="px-4 py-2.5 border-t border-border flex justify-end">
        <Button
          data-testid={`${testIdPrefix}-save`}
          disabled={mut.isPending || !dirty}
          onClick={() => mut.mutate()}
          className="bg-primary hover:bg-primary/90"
          size="sm"
        >
          <Save className="size-3.5 mr-1.5" />
          {mut.isPending ? "Saving…" : "Save"}
        </Button>
      </footer>
    </section>
  );
}
